<script setup lang="ts">
/**
 * This component is not final and will be changed here
 * https://tinyurl-llc.atlassian.net/browse/TEAM-159
 */

import { computed } from "vue";
import { useRouter, useRoute } from "vue-router/composables";
import { isNullish, TuGhostNavbar, TuGuestNavbar, TuSubscribedNavbar } from "@tinyurldev/tu-ui-vue";

import { XTeamsStore } from "@Modules/teams/Store/XTeamsStore";
import { XUserTeamStore } from "@Modules/teams/Store/XUserTeamStore";
import { XAuthStore } from "@Modules/users/Store/XAuthStore";
import { XShoppingCartStore } from "@Modules/shoppingcart/Store/XShoppingCartStore";
import useLegacyStore from "@Composables/useLegacyStore";
import useSubscription from "@Composables/useSubscription";
import useAuth from "@Composables/useAuth";
import useTranslation from "@Composables/useTranslation";
import { useCurrentOrganization } from "@Modules/teams/composables/useCurrentOrganization";
import XOverlay from "@Modules/shared/store/XOverlay";

import { NavbarVariant } from "./navbar-variant";
import useNavigationButtons, { availableButtons } from "./useNavigationButtons";
import { featuresMenuOptions, resourcesMenuOptions } from "./navbar-context";
import NameToInitials from "@Resources/js/filters/NameToInitials";
import type { Organization } from "@Modules/teams/Data/Models/OrganizationModel";

//
// Setup
const availableMenuOptions = {
  PLANS: "plans",
  FEATURES: "features",
  DOMAINS: "domains",
  RESOURCES: "resources",
  DASHBOARD: "dashboard",
  SHORTEN: "shorten",
};

const { currentOrganizationSlug, currentTeamSlug, currentOrganization } = useCurrentOrganization();
const teamsStore = XTeamsStore();
const userTeamStore = XUserTeamStore();
const authStore = XAuthStore();
const cartStore = XShoppingCartStore();
const route = useRoute();
const router = useRouter();
const store = useLegacyStore()!;
const { getSubscriptionName } = useSubscription();
const { asyncLogout } = useAuth();
const { __ } = useTranslation();
const { helpButton, kioskButton, notificationButton, shoppingCartButton, returnKioskButton } = useNavigationButtons();
const overlay = XOverlay();

//
// Props
const props = defineProps<{ variant: string }>();

//
// Computed
const isUserLoggedIn = computed(() => store.getters.isUserLoggedIn);
const isUserFree = computed(() => authStore.userModel.subscription.isFree());

const currentNavbar = computed(() => {
  if (props.variant === NavbarVariant.Ghost) {
    return "ghost";
  }

  if (props.variant === NavbarVariant.Logo) {
    return "logo";
  }

  if (props.variant === NavbarVariant.Guest || !isUserLoggedIn.value) {
    return "guest";
  }

  if (props.variant === NavbarVariant.Free || isUserFree.value) {
    return "free";
  }

  return "subscribed";
});

const isTeamsMode = computed(() => !isNullish(currentOrganizationSlug.value) || !isNullish(currentTeamSlug.value));
const accountProperties = computed(() => {
  const switchableOptions = prepareOrganizations(Object.values(teamsStore.organizations) as Organization[]);
  const user = authStore.user;

  return {
    excerpt: NameToInitials(user?.name ?? ""),
    userName: user?.name ?? "",
    email: user?.email ?? "",
    src: getProfilePicture(user?.photo_url),
    plan: isUserFree.value ? __("navbar.plan_free") : getSubscriptionName(store.getters.getUserCurrentSubscription),
    logoutText: __("navbar.logout_text"),
    upgradeButton: { label: __("navbar.upgrade_button"), as: "router-link", to: { name: "signup.pricing" } },
    manageButton: { label: __("navbar.manage_account_button"), as: "router-link", to: { name: "settings" } },
    switchableOptions,
    ...(switchableOptions.length > 0 && { footerTitle: __("navbar.switch_options")}),
  };
});

const teamAccountProperties = computed(() => {
  const user = authStore.user;
  const org = currentOrganization.value as Organization | null;
  const organizations = Object.values(teamsStore.organizations) as Organization[];

  const defaultProps = {
    excerpt: NameToInitials(org?.name ?? ""),
    userName: org?.name ?? "",
    email: user.email,
    src: getProfilePicture(org?.photo_url),
    plan: __("navbar.plan_teams"),
    logoutText: __("navbar.logout_text"),
    manageButton: { label: __("navbar.manage_profile_button"), as: "router-link", to: { name: "org.settings.user.profile", params: { org: org?.slug } } },
  };

  // Filter out current organization
  const otherOrgs = organizations.filter((organization) => organization.slug !== currentOrganizationSlug.value);

  // Check if user has a default organization set (personal option only shown if no default org)
  const hasDefaultOrg = userTeamStore.defaultOrganization !== null;

  // If there's a default org, show orgs only; otherwise show personal first
  const switchableOptions = hasDefaultOrg
    ? prepareOrganizations(otherOrgs)
    : [
        {
          id: "personal",
          title: user.name,
          helper: __("navbar.personal_account_helper"),
          excerpt: NameToInitials(user.name),
          src: getProfilePicture(user?.photo_url),
        },
        ...prepareOrganizations(otherOrgs),
      ];

  return {
    ...defaultProps,
    switchableOptions,
    ...(switchableOptions.length > 0 && { footerTitle: __("navbar.switch_options")}),
  };
});

/**
 * In teams mode the cart is an org scoped popover, but a route without an org slug has no org cart
 * loaded, so the button is hidden rather than acting on the personal cart under the teams navbar.
 * It is also hidden while the cart is empty, paid users included.
 */
const isCartVisible = computed(() => {
  if (isTeamsMode.value && isNullish(currentOrganizationSlug.value)) {
    return false;
  }

  return isUserLoggedIn.value && cartStore.quantity > 0;
});

const subscribedNavbarButtons = computed(() => {
  const buttons = [helpButton.value];

  if (isCartVisible.value) {
    if (isTeamsMode.value) {
      const { attributes, as, ...cartButtonWithoutLink } = shoppingCartButton.value;

      buttons.push(cartButtonWithoutLink);
    } else {
      buttons.push(shoppingCartButton.value);
    }
  }

  if (store.getters.isDeveloper) {
    buttons.push(kioskButton.value);
  }

  if (authStore.isImpersonated) {
    buttons.push(returnKioskButton.value);
  }

  buttons.push(notificationButton.value);

  return buttons;
});

const guestMenuOptions = computed(() => [
  {
    id: availableMenuOptions.PLANS,
    title: __("navbar.menu_plans"),
    as: "router-link",
    attributes: {
      to: { name: "signup.pricing" },
      activeClass: "active",
    },
  },
  {
    id: availableMenuOptions.FEATURES,
    title: __("navbar.menu_features"),
    isActive: route.name?.includes("features"),
    heroMenuProperty: {
      title: __("navbar.menu_features"),
      options: featuresMenuOptions.map((option) => ({
        ...option,
        as: "router-link",
        attributes: { ...option.attributes, activeClass: "active" },
      })),
    },
  },
  {
    id: availableMenuOptions.DOMAINS,
    title: __("navbar.menu_domains"),
    as: "router-link",
    attributes: {
      to: { name: "branded-domains" },
      activeClass: "active",
    },
  },
  {
    id: availableMenuOptions.RESOURCES,
    title: __("navbar.menu_resources"),
    isActive: route.name?.includes("resources"),
    heroMenuProperty: {
      title: __("navbar.menu_resources"),
      options: resourcesMenuOptions.map((option) => ({
        ...option,
        as: "router-link",
        attributes: { ...option.attributes, activeClass: "active" },
      })),
    },
  },
]);

const dashboardButton = computed(() => {
  const defaultProperties = {
    id: availableMenuOptions.DASHBOARD,
    title: __("navbar.menu_dashboard"),
  };

  if (!isTeamsMode.value) {
    return {
      ...defaultProperties,
        as: "router-link",
        attributes: { to: { name: "dashboard" }},
    };
  }

  return defaultProperties;
});

const shortLinkButton = computed(() => {
  const defaultProperties = {
    id: availableMenuOptions.SHORTEN,
    title: __("navbar.menu_shorten"),
  };

  if (!isTeamsMode.value) {
    return {
      ...defaultProperties,
      as: "router-link",
      attributes: {
        to: {
          name: "dashboard",
          params: { openCreate: true },
        },
      },
    };
  }

  return defaultProperties;
});

const subscribedMenuOptions = computed(() => [
    dashboardButton.value,
    {
      id: availableMenuOptions.DOMAINS,
      title: __("navbar.menu_domains"),
      as: "router-link",
      attributes: {
        to: { name: isTeamsMode.value ? "org.settings.profile.domains" : "settings.brandedDomains" },
      },
    },
    shortLinkButton.value,
  ],
);

//
// Methods
function prepareOrganizations(organizations: Organization[]) {
  return organizations.map((organization) => ({
    id: organization.slug,
    title: organization.name,
    helper: __("navbar.org_helper"),
    excerpt: NameToInitials(organization.name),
    src: getProfilePicture(organization?.photo_url),
  }));
}

function getProfilePicture(src?: string) {
  if (isNullish(src) || src.includes("user.svg")) {
    return null;
  }

  return src;
}

function onSubscribedOptionClicked(optionId: string) {
  if (optionId === availableMenuOptions.SHORTEN) {
    overlay.showCreateFormPopover();

    return;
  }

  if (optionId === availableMenuOptions.DASHBOARD) {
    currentOrganization.value?.visit();

    return;
  }
}

function onSubscribedButtonClicked(buttonId: string) {
  if (buttonId === availableButtons.NOTIFICATION) {
    overlay.showNotificationsPopover();

    return;
  }

  if (buttonId === availableButtons.SHOPPING_CART && isTeamsMode.value) {
    overlay.showShoppingCartPopover();

    return;
  }
}

function onSwitchAccount(id: string) {
  id === "personal"
    ? router.push({ name: "homepage" })
    : teamsStore.organizations[id].visit();
}

function onHomeIconClicked() {
  if (isTeamsMode.value) {
    currentOrganization.value?.visit();

    return;
  }

  router.push({ name: "homepage" });
}

//
// Exposed Test Vars
defineExpose({
  currentNavbar,
  guestMenuOptions,
  subscribedMenuOptions,
  subscribedNavbarButtons,
  teamAccountProperties,
});
</script>

<template>
  <div
    id="application-navbar"
    tabindex="-1"
    class="application-navbar _navbar"
    :class="{
      'application-navbar--ghost': currentNavbar === 'ghost' || currentNavbar === 'logo',
      'application-navbar--logo': currentNavbar === 'logo',
    }"
  >
    <!-- Ghost Navbar: Minimal navbar for auth flows. The logo variant is the
         same bar with its close button hidden by the style below. -->
    <TuGhostNavbar
      v-if="currentNavbar === 'ghost' || currentNavbar === 'logo'"
      @close="router.push({ name: 'homepage' })"
      @icon-click="onHomeIconClicked"
    />

    <!-- Guest Navbar: For unauthenticated users -->
    <TuGuestNavbar
      v-if="currentNavbar === 'guest'"
      :options="guestMenuOptions"
      :login-text="__('navbar.login_text')"
      :signup-text="__('navbar.signup_text')"
      @login="router.push({ name: 'login' })"
      @sign-up="router.push({ name: 'register' })"
      @icon-click="onHomeIconClicked"
    />

    <!-- Subscribed Navbar: For free users -->
    <TuSubscribedNavbar
      v-if="currentNavbar === 'free'"
      :options="isTeamsMode ? subscribedMenuOptions : guestMenuOptions"
      :buttons="subscribedNavbarButtons"
      :account-properties="isTeamsMode ? teamAccountProperties : accountProperties"
      :logout-handler="asyncLogout"
      @option-clicked="onSubscribedOptionClicked"
      @button-clicked="onSubscribedButtonClicked"
      @icon-click="onHomeIconClicked"
      @switch-account="onSwitchAccount"
    />

    <!-- Subscribed Navbar: For authenticated users -->
    <TuSubscribedNavbar
      v-if="currentNavbar === 'subscribed'"
      :options="subscribedMenuOptions"
      :buttons="subscribedNavbarButtons"
      :account-properties="isTeamsMode ? teamAccountProperties : accountProperties"
      :logout-handler="asyncLogout"
      @option-clicked="onSubscribedOptionClicked"
      @button-clicked="onSubscribedButtonClicked"
      @icon-click="onHomeIconClicked"
      @switch-account="onSwitchAccount"
    />
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/position.scss" as position;

.application-navbar {
  position: sticky;
  top: 0px;
  z-index: position.$highest-z-index;

  &:focus-visible {
    outline: none;
    box-shadow: none;
  }

  &--ghost {
    position: absolute;
    top: 0px;

    width: 100%;
  }
}
</style>

<style lang="scss">
.application-navbar--logo .tu-ghost-navbar__close-button {
  display: none;
}
</style>
